import {computed, inject, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ProfileState, UserProfile} from '@epg-apps/cdh-models';
import {environment} from '@epg-apps/cdh-environments';
import {catchError, Observable, of, tap} from 'rxjs';
import {toObservable} from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private readonly http = inject(HttpClient);

  private static readonly PROFILE_IAM_API = environment.iamApi + 'profile';

  private readonly profileState = signal<ProfileState>({
    userProfile: undefined,
    isLoading: false,
  });

  readonly userProfile = computed<UserProfile | undefined>(
    () => this.profileState().userProfile,
  );
  readonly isLoadingUserProfile = computed<boolean>(
    () => this.profileState().isLoading,
  );
  private readonly userProfileObservable: Observable<UserProfile | undefined> =
    toObservable(this.userProfile);

  loadUserProfileAsObservable(): Observable<UserProfile | undefined> {
    return this.getUserProfile().pipe(
      tap((profile) => {
        this.setIsLoading(false);
        this.setUserProfile(profile);
      }),
    );
  }

  getUserProfile(): Observable<UserProfile | undefined> {
    if (this.userProfile() !== undefined) {
      return this.userProfileObservable;
    } else {
      this.setIsLoading(true);
      return this.http.get<UserProfile>(ProfileService.PROFILE_IAM_API).pipe(
        catchError((error) => {
          console.error('Error fetching user profile:', error);
          this.setIsLoading(false);
          return of(undefined);
        }),
      );
    }
  }

  setUserProfile(userProfile?: UserProfile): void {
    this.profileState.update((state) => ({
      ...state,
      userProfile: userProfile,
    }));
  }

  setIsLoading(isLoading: boolean): void {
    this.profileState.update((state) => ({
      ...state,
      isLoading: isLoading,
    }));
  }
}
